// src/components/AlertNotification.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, X, Eye, ShieldAlert } from 'lucide-react';
import { eventEmitter } from '../utils/eventEmitter';

const AlertNotification = () => {
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = eventEmitter.on('newAlert', (alertData) => {
      const notification = {
        id: alertData._id || `alert_${Date.now()}`,
        ...alertData,
        timestamp: Date.now(),
        isVisible: true,
        isRead: false
      };
      
      setAlerts(prev => [notification, ...prev].slice(0, 5));
      
      const timeoutId = setTimeout(() => {
        setAlerts(prev => prev.filter(a => a.id !== notification.id));
      }, 15000);

      notification._timeoutId = timeoutId;
    });

    return () => {
      unsubscribe();
      setAlerts(prev => {
        prev.forEach(a => {
          if (a._timeoutId) clearTimeout(a._timeoutId);
        });
        return [];
      });
    };
  }, []);

  const handleClick = (alert) => {
    // Clear the auto-dismiss timeout
    if (alert._timeoutId) {
      clearTimeout(alert._timeoutId);
    }
    
    // Remove from notifications
    setAlerts(prev => prev.filter(a => a.id !== alert.id));
    
    const alertId = alert.id;
    
    // ✅ Check if we're already on the Alerts page
    const isOnAlertsPage = location.pathname.includes('/alerts') || 
                           location.pathname.includes('/prestigehome/alerts');
    
    if (isOnAlertsPage) {
      // ✅ Already on Alerts page - just emit a highlight event
      eventEmitter.emit('highlightAlert', {
        alertId: alertId,
        alertData: alert,
        openSnapshot: true // ✅ Open snapshot when clicking notification
      });
    } else {
      // ✅ On different page - navigate with state
      navigate('/prestigehome/alerts', { 
        state: { 
          highlightAlert: alertId,
          alertData: alert,
          openSnapshot: true // ✅ Pass flag to open snapshot
        } 
      });
    }
  };

  const handleDismiss = (alertId, e) => {
    e.stopPropagation();
    setAlerts(prev => {
      const alert = prev.find(a => a.id === alertId);
      if (alert && alert._timeoutId) {
        clearTimeout(alert._timeoutId);
      }
      return prev.filter(a => a.id !== alertId);
    });
  };

  if (alerts.length === 0) return null;

  return (
    <div className="fixed top-24 right-6 z-50 flex flex-col gap-3 max-w-md w-full pointer-events-none">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`pointer-events-auto animate-slide-in-right bg-white rounded-xl shadow-2xl border-l-4 ${
            alert.type === 'intrusion' 
              ? 'border-red-500' 
              : alert.type === 'loitering'
              ? 'border-amber-500'
              : 'border-blue-500'
          } overflow-hidden transform transition-all duration-300 hover:scale-[1.02] cursor-pointer group`}
          onClick={() => handleClick(alert)}
        >
          <div className="p-4 flex items-start gap-3">
            {/* Icon */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              alert.type === 'intrusion' 
                ? 'bg-red-50 text-red-500' 
                : 'bg-amber-50 text-amber-500'
            }`}>
              {alert.type === 'intrusion' 
                ? <AlertTriangle size={20} /> 
                : <ShieldAlert size={20} />
              }
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-slate-800 text-sm">
                    {alert.type === 'intrusion' ? '🚨 Intrusion Alert' : '⚠️ Loitering Alert'}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Camera: {alert.camera_id || 'Unknown'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {alert.timestamp ? new Date(alert.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString()}
                  </p>
                </div>
                <button
                  onClick={(e) => handleDismiss(alert.id, e)}
                  className="flex-shrink-0 p-1 hover:bg-slate-100 rounded-md transition-colors"
                >
                  <X size={16} className="text-slate-400" />
                </button>
              </div>

              {/* Action button */}
              <div className="mt-2 flex items-center gap-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(alert);
                  }}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 group-hover:underline"
                >
                  <Eye size={12} />
                  View Alert
                </button>
              </div>
            </div>
          </div>

          {/* Progress bar for auto-dismiss */}
          <div className="h-0.5 bg-slate-100 relative overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-red-400 to-amber-400 animate-progress"
              style={{ animationDuration: '15s' }}
            />
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes progress {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
        
        .animate-progress {
          animation: progress linear forwards;
        }
      `}</style>
    </div>
  );
};

export default AlertNotification;